<template>
    <v-card flat min-height="200" class="import-visualizer-card d-flex flex-column">
        <template v-if="importInProgress">
            <div class="d-flex flex-column align-center justify-center pa-4 flex-grow-1">
                <v-progress-circular indeterminate color="primary" size="48" class="mb-3"></v-progress-circular>
                <h3 class="mb-1">{{ displayStepLabel }}</h3>
                <v-chip v-if="currentStepIndex >= 0" small outlined color="primary" class="mt-1 rounded-0"
                    style="border-left: none; border-right: none;">
                    Step {{ currentStepIndex + 1 }} of {{ steps.length }}
                </v-chip>
            </div>
        </template>
        <template v-else-if="importSuccess">
            <div class="d-flex flex-column align-center justify-center pa-4 flex-grow-1">
                <v-icon color="success" size="56" class="mb-2">
                    mdi-check-circle
                </v-icon>
                <h3>Import successful</h3>
                <v-btn color="primary" class="mt-3" @click="reset()">
                    Upload another file
                </v-btn>
            </div>
        </template>
        <template v-else-if="importError">
            <div class="d-flex flex-column align-center pa-4 flex-grow-1">
                <v-icon color="error" size="56" class="mb-2">
                    mdi-alert-circle
                </v-icon>
                <h3 class="mb-2">Import failed</h3>
                <p class="error-summary text-center mb-3">
                    {{ errorSummary }}
                </p>
                <v-expansion-panels flat class="mb-3" style="max-width: 100%;">
                    <v-expansion-panel>
                        <v-expansion-panel-header class="py-2">
                           Technical details
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <pre class="error-details pa-2">{{ fullErrorMessage }}</pre>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
                <v-btn color="primary" @click="reset()">
                    Upload another file
                </v-btn>
            </div>
        </template>
        <template v-else>
            <div class="d-flex flex-column align-center justify-center flex-grow-1" style="min-height: inherit;">
                <template v-if="uploadFile">
                    <v-progress-circular indeterminate color="primary" size="40" class="mb-3"></v-progress-circular>
                    <p class="mb-2">Uploading file to server...</p>
                    <small class="d-block mb-3">File will be processed automatically once uploaded</small>
                    <v-chip color="primary" dark label small>{{ uploadFileName }}</v-chip>
                </template>
                <template v-else>
                    <drop-pad accept=".xlsx,.xls,.xml" :outlined="true" :disabled="false" @fileDrop="handleFileDrop"
                        @click="clickFileInput()">
                        <template v-slot:default>
                            <div class="text-center py-5 px-12">
                                <v-icon size="70" class="mb-3">mdi-file-upload-outline</v-icon>
                                <h3 class="mb-2">Drop file here or click to browse</h3>
                                <small>Excel (.xlsx, .xls) or XML</small>
                            </div>
                        </template>
                    </drop-pad>
                    <input type="file" ref="hiddenFileInput" accept=".xlsx,.xls,.xml" style="display: none"
                        @change="onFileSelected" />
                </template>
            </div>
        </template>
    </v-card>
</template>


<script>
import ClientAPIMixin from '@/api/ClientAPIMixin';
import DropPad from "@/components/SingleUse/DropPad.vue";

const DEFAULT_IMPORT_STEPS = [
    "Starting import",
    "Importing Suppliers",
    "Importing Articles",
    "Importing Customers",
    "Importing CustomerPersons",
    "Importing Departments",
    "Importing Employments",
    "Importing Stores",
    "Importing StoreLocations",
    "Importing StoreItems",
    "Importing Machines",
    "End Import",
];

export default {
    name: 'ImportVisualizer',
    props: {
        importInProgress: Boolean,
        progressText: String,
        importError: Object,
        importSuccess: Object,
        importSteps: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        DropPad,
    },
    mixins: [ClientAPIMixin],

    data() {
        return {
            uploadFile: null,
        };
    },

    computed: {
        steps() {
            return (this.importSteps && this.importSteps.length > 0)
                ? this.importSteps
                : DEFAULT_IMPORT_STEPS;
        },
        currentStepIndex() {
            if (!this.progressText) return -1;
            const text = (this.progressText || "").trim();
            const idx = this.steps.findIndex(s => text.startsWith(s) || text === s);
            return idx >= 0 ? idx : this.steps.length - 1;
        },
        displayStepLabel() {
            const idx = this.currentStepIndex;
            if (idx >= 0 && idx < this.steps.length) {
                return this.steps[idx];
            }
            return "Import in progress";
        },
        errorSummary() {
            if (!this.importError?.message) return "An error occurred during import.";
            const msg = String(this.importError.message);
            const firstLine = msg.split(/\r?\n/)[0] || msg;
            if (firstLine.length <= 120) return firstLine;
            return firstLine.substring(0, 117) + "...";
        },
        fullErrorMessage() {
            return this.importError?.message || "";
        },
        uploadFileName() {
            if (!this.uploadFile) return "";
            return typeof this.uploadFile === 'object' && this.uploadFile.name
                ? this.uploadFile.name
                : String(this.uploadFile);
        },
    },

    methods: {
        reset() {
            this.uploadFile = null;
            this.$emit('reset');
        },

        handleFileDrop(file) {
            if (file) this.uploadFile = file;
        },

        clickFileInput() {
            this.$refs.hiddenFileInput?.click();
        },

        onFileSelected(e) {
            const files = e.target.files;
            if (files && files.length) {
                this.uploadFile = files[0];
            }
            e.target.value = "";
        },

        async handleSave() {
            if (!this.uploadFile) return;
            const file = this.uploadFile;
            try {
                await this.importFile(file);
            } finally {
                if (this.uploadFile === file) {
                    this.uploadFile = null;
                }
            }
        },
    },

    watch: {
        uploadFile(val) {
            if (val) this.handleSave();
        },
    },
};
</script>


<style lang='scss' scoped>
.import-visualizer-card {
    transition: min-height 0.2s ease;
}

.import-visualizer-card h3 {
    margin: 0;
    font-weight: inherit;
}

.error-details {
    font-size: 11px;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: min(500px, 60vh);
    overflow-y: auto;
    overflow-x: auto;
    margin: 0;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 4px;
}
</style>
